import React from 'react';
import Icon from '../AppIcon';

const ProgressTracker = ({
  progress = 0,
  status = 'processing',
  label,
  estimatedTime,
  showCancel = false,
  onCancel,
  variant = 'default',
  size = 'default',
  className = ''
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'completed':
        return {
          color: 'text-success',
          bgColor: 'bg-success',
          icon: 'CheckCircle'
        };
      case 'error':
        return {
          color: 'text-error',
          bgColor: 'bg-error',
          icon: 'XCircle'
        };
      case 'cancelled':
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted-foreground',
          icon: 'XCircle'
        };
      case 'paused':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning',
          icon: 'Pause'
        };
      default:
        return {
          color: 'text-primary',
          bgColor: 'bg-primary',
          icon: 'Loader2'
        };
    }
  };

  const getSizeConfig = () => {
    switch (size) {
      case 'sm':
        return {
          height: 'h-1',
          text: 'text-xs',
          icon: 14,
          padding: 'p-2'
        };
      case 'lg':
        return {
          height: 'h-3',
          text: 'text-base',
          icon: 24,
          padding: 'p-4'
        };
      default:
        return {
          height: 'h-2',
          text: 'text-sm',
          icon: 18,
          padding: 'p-3'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const sizeConfig = getSizeConfig();
  const progressPercentage = Math.min(Math.max(progress, 0), 100);

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '';
    
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const renderLinearProgress = () => (
    <div className={`w-full ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Icon 
            name={statusConfig?.icon} 
            size={sizeConfig?.icon}
            className={`${statusConfig?.color} ${status === 'processing' ? 'animate-spin' : ''}`}
          />
          {label && (
            <span className={`font-medium ${statusConfig?.color} ${sizeConfig?.text}`}>
              {label}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          {estimatedTime && status === 'processing' && (
            <span className={`${sizeConfig?.text} text-muted-foreground`}>
              {formatTime(estimatedTime)} remaining
            </span>
          )}
          <span className={`font-mono ${sizeConfig?.text} ${statusConfig?.color}`}>
            {Math.round(progressPercentage)}%
          </span>
          {showCancel && onCancel && status === 'processing' && (
            <button
              onClick={onCancel}
              className="p-1 rounded hover:bg-muted transition-smooth"
              title="Cancel operation"
            >
              <Icon name="X" size={sizeConfig?.icon - 2} className="text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className={`w-full bg-muted rounded-full overflow-hidden ${sizeConfig?.height}`}>
        <div 
          className={`${sizeConfig?.height} ${statusConfig?.bgColor} transition-all duration-300 ease-out rounded-full`}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </div>
  );

  const renderCircularProgress = () => {
    const radius = size === 'sm' ? 16 : size === 'lg' ? 32 : 24;
    const strokeWidth = size === 'sm' ? 2 : size === 'lg' ? 4 : 3;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

    return (
      <div className={`inline-flex flex-col items-center space-y-2 ${className}`}>
        <div className="relative">
          <svg
            height={radius * 2}
            width={radius * 2}
            className="transform -rotate-90"
          >
            {/* Background circle */}
            <circle
              stroke="currentColor"
              className="text-muted"
              fill="transparent"
              strokeWidth={strokeWidth}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
            {/* Progress circle */}
            <circle
              stroke="currentColor"
              className={statusConfig?.color}
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              style={{ strokeDashoffset }}
              strokeLinecap="round"
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
          </svg>
          
          {/* Center content */}
          <div className="absolute inset-0 flex items-center justify-center">
            {status === 'processing' ? (
              <Icon 
                name="Loader2" 
                size={sizeConfig?.icon - 4}
                className={`${statusConfig?.color} animate-spin`}
              />
            ) : (
              <Icon 
                name={statusConfig?.icon} 
                size={sizeConfig?.icon - 4}
                className={statusConfig?.color}
              />
            )}
          </div>
        </div>
        {/* Label and percentage */}
        <div className="text-center">
          <div className={`font-mono font-medium ${statusConfig?.color} ${sizeConfig?.text}`}>
            {Math.round(progressPercentage)}%
          </div>
          {label && (
            <div className={`${sizeConfig?.text} text-muted-foreground mt-1`}>
              {label}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCompactProgress = () => (
    <div className={`inline-flex items-center space-x-2 ${className}`}>
      <Icon 
        name={statusConfig?.icon} 
        size={sizeConfig?.icon}
        className={`${statusConfig?.color} ${status === 'processing' ? 'animate-spin' : ''}`}
      />
      <div className={`w-20 bg-muted rounded-full overflow-hidden ${sizeConfig?.height}`}>
        <div 
          className={`${sizeConfig?.height} ${statusConfig?.bgColor} transition-all duration-300 ease-out`}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
      <span className={`font-mono ${sizeConfig?.text} ${statusConfig?.color} min-w-[3rem] text-right`}>
        {Math.round(progressPercentage)}%
      </span>
    </div>
  );

  switch (variant) {
    case 'circular':
      return renderCircularProgress();
    case 'compact':
      return renderCompactProgress();
    default:
      return renderLinearProgress();
  }
};

export default ProgressTracker;